import { Link, usePage } from "@inertiajs/react";
import {
  LayoutDashboard,
  Users,
  Building2,
  ShieldAlert,
  Briefcase,
  BarChart2,
  Mail,
  Settings,
  Zap,
  BookOpen,
  Target,
  Menu,
  X,
  LogOut,
} from "lucide-react";
import { ReactNode, useEffect, useState } from "react";
import AskDocket from "../components/AskDocket";

function getCsrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";
}

const NAV_GROUPS = [
  {
    heading: null,
    items: [
      { label: "Dashboard",  href: "/dashboard",  icon: LayoutDashboard },
      { label: "Candidates", href: "/candidates", icon: Users },
      { label: "Firms",      href: "/firms",      icon: Building2 },
      { label: "DNC",        href: "/dnc",        icon: ShieldAlert },
      { label: "Analytics",  href: "/analytics",  icon: BarChart2 },
    ],
  },
  {
    heading: "Sourcing",
    items: [
      { label: "Job Alerts",       href: "/job-alerts",       icon: Zap },
      { label: "Email Campaigns",  href: "/email-campaigns",  icon: Mail },
    ],
  },
  {
    heading: "Pipeline",
    items: [
      { label: "Pipeline", href: "/pipeline", icon: Briefcase },
      { label: "Packages", href: "/packages", icon: BookOpen },
      { label: "SKM",      href: "/skm",      icon: Target },
    ],
  },
];

interface Props {
  children: ReactNode;
  title?: string;
}

function SidebarContent({
  user,
  currentPath,
  onNavigate,
}: {
  user: { name: string; role: string } | null;
  currentPath: string;
  onNavigate?: () => void;
}) {
  return (
    <>
      {/* Logo */}
      <div className="px-6 py-5 border-b border-gray-700/50">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded bg-green-700 flex items-center justify-center">
            <span className="text-white text-xs font-bold">D</span>
          </div>
          <span className="text-white font-semibold text-lg tracking-tight">Docket by TTS</span>
        </div>
        <p className="text-gray-500 text-xs mt-1">Rushing Recruiting</p>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        {NAV_GROUPS.map((group, gi) => (
          <div key={gi} className={gi > 0 ? "mt-5" : ""}>
            {group.heading && (
              <p className="px-3 mb-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">
                {group.heading}
              </p>
            )}
            <div className="space-y-0.5">
            {group.items.map(({ label, href, icon: Icon }) => {
          const active =
            currentPath === href ||
            currentPath.startsWith(href + "?") ||
            (href !== "/dashboard" && currentPath.startsWith(href));
          return (
            <Link
              key={label}
              href={href}
              onClick={onNavigate}
              className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                active
                  ? "bg-green-800/70 text-white"
                  : "text-gray-400 hover:text-white hover:bg-gray-800"
              }`}
            >
              <Icon size={16} className={active ? "text-green-400" : ""} />
              {label}
            </Link>
          );
        })}
            </div>
          </div>
        ))}
      </nav>

      {/* Bottom: Settings + user */}
      <div className="px-3 pb-4 border-t border-gray-700/50 pt-3 space-y-0.5">
        {user?.role === "admin" && (
          <Link
            href="/settings"
            onClick={onNavigate}
            className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
          >
            <Settings size={16} />
            Settings
          </Link>
        )}
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="w-6 h-6 rounded-full bg-green-800 flex items-center justify-center flex-shrink-0">
            <span className="text-green-200 text-xs font-medium">
              {user?.name?.charAt(0) ?? "?"}
            </span>
          </div>
          <div className="min-w-0">
            <p className="text-gray-300 text-xs font-medium truncate">{user?.name}</p>
            <p className="text-gray-500 text-xs capitalize">{user?.role}</p>
          </div>
        </div>
        <form action="/session" method="post">
          <input type="hidden" name="_method" value="delete" />
          <input type="hidden" name="authenticity_token" value={getCsrfToken()} />
          <button
            type="submit"
            className="flex items-center gap-3 px-3 py-2 w-full rounded-md text-sm text-gray-500 hover:text-red-400 hover:bg-gray-800 transition-colors"
          >
            <LogOut size={16} />
            Sign out
          </button>
        </form>
        <div className="flex gap-3 px-3 pt-2">
          <a href="/privacy" className="text-gray-600 hover:text-gray-400 text-xs transition-colors">Privacy</a>
          <a href="/terms" className="text-gray-600 hover:text-gray-400 text-xs transition-colors">Terms</a>
        </div>
      </div>
    </>
  );
}

function FlashBanner({ message, variant }: { message: string; variant: "notice" | "alert" }) {
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDismissed(true), 5000);
    return () => clearTimeout(t);
  }, []);

  if (dismissed) return null;

  const styles =
    variant === "alert"
      ? "bg-red-50 text-red-700 border-b border-red-200"
      : "bg-green-50 text-green-700 border-b border-green-200";

  return (
    <div className={`flex items-center justify-between px-4 py-2 text-sm font-medium flex-shrink-0 ${styles}`}>
      <span>{message}</span>
      <button onClick={() => setDismissed(true)} className="ml-4 opacity-60 hover:opacity-100 transition-opacity">
        <X size={14} />
      </button>
    </div>
  );
}

interface EmailSafetyShared {
  kill_switch_on: boolean;
  test_mode_on: boolean;
}

export default function AppLayout({ children, title }: Props) {
  const { auth, email_safety, google_revoked_at, flash } = usePage<{
    auth: { user: { name: string; role: string } | null };
    email_safety: EmailSafetyShared;
    google_revoked_at: string | null;
    flash: { notice?: string | null; alert?: string | null };
  }>().props;
  const user = auth?.user;
  const currentPath = typeof window !== "undefined" ? window.location.pathname : "";
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="flex h-screen bg-gray-50 relative">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-shrink-0 bg-gray-900 flex-col">
        <SidebarContent user={user} currentPath={currentPath} />
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            onClick={() => setMobileOpen(false)}
          />
          {/* Drawer */}
          <aside className="fixed inset-y-0 left-0 z-50 w-64 bg-gray-900 flex flex-col lg:hidden">
            <SidebarContent
              user={user}
              currentPath={currentPath}
              onNavigate={() => setMobileOpen(false)}
            />
          </aside>
        </>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="h-14 border-b border-gray-200 bg-white flex items-center px-4 lg:px-6 gap-3 flex-shrink-0">
          {/* Hamburger — mobile only */}
          <button
            className="lg:hidden text-gray-500 hover:text-gray-700 p-1 -ml-1"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={20} />
          </button>
          {/* Title shown on mobile only — desktop has sidebar nav for context */}
          <h1 className="lg:hidden text-gray-800 font-medium text-sm">{title ?? "Docket"}</h1>
        </header>

        {/* Email safety banner */}
        {email_safety?.kill_switch_on && (
          <div className="bg-red-600 text-white text-xs font-medium px-4 py-2 flex items-center gap-2 flex-shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-white/80 flex-shrink-0" />
            Email sending is globally disabled — no mail will go out.
          </div>
        )}
        {!email_safety?.kill_switch_on && email_safety?.test_mode_on && (
          <div className="bg-amber-500 text-white text-xs font-medium px-4 py-2 flex items-center gap-2 flex-shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-white/80 flex-shrink-0" />
            Email test mode is active — all mail redirected to test recipients.
          </div>
        )}
        {google_revoked_at && (
          <div className="bg-red-600 text-white text-xs font-medium px-4 py-2 flex items-center gap-2 flex-shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-white/80 flex-shrink-0" />
            ⚠ Google integration disconnected — Gmail, Calendar, and DNC Sheet sync are paused. No new Job Alert emails will be received while disconnected.{" "}
            <Link href="/settings" className="underline hover:no-underline">
              Reconnect →
            </Link>
          </div>
        )}

        {/* Flash messages */}
        {flash?.notice && <FlashBanner key={flash.notice} message={flash.notice} variant="notice" />}
        {flash?.alert && <FlashBanner key={flash.alert} message={flash.alert} variant="alert" />}

        {/* Content */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          {children}
        </main>
      </div>
      {user?.role === "admin" && <AskDocket />}
    </div>
  );
}
